import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, Typography, Stack } from "@mui/material";
import { useTheme, type SxProps, type Theme } from "@mui/material/styles";
import React from "react";
import { ClientOnly } from "../ClientOnly";
import {
  centerModalContentBaseSx,
  centerModalHeaderBaseSx,
  getCenterModalSizeTokens,
  resolveCenterModalWidth,
  type CenterModalSize,
} from "./tokens";

interface CenterModalProps {
  open: boolean;
  onClose: (event?: any, reason?: "backdropClick" | "escapeKeyDown") => void;
  title: string;
  children: React.ReactNode;
  /** Default width follows `size`; pass a number or object to override `md` (and optionally `xs`). */
  width?: number | { xs?: number | string; md?: number | string };
  /** Controls default `md` width and title `font-size` scale (rem). */
  size?: CenterModalSize;
  hideCloseButton?: boolean;
  /** Merged into the outer card `Box` (e.g. Figma card border + radius). */
  paperSx?: SxProps<Theme>;
  /** Merged into the title row `Stack`. */
  headerSx?: SxProps<Theme>;
  /** Merged into the scrollable content `Box`. */
  contentSx?: SxProps<Theme>;
  /** Merged into the title `Typography` (after base styles). */
  titleSx?: SxProps<Theme>;
  /** Merged into the close `IconButton` when shown. */
  closeButtonSx?: SxProps<Theme>;
  /** When true, clicks on the backdrop do not call `onClose` (Figma: confirmation modals). */
  disableBackdropClick?: boolean;
}

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  borderRadius: "var(--Card-Radius-Medium)",
  boxShadow: "0px 2px 40px rgba(0, 0, 0, 0.1)",
  p: 0,
  outline: "none",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const CenterModal: React.FC<CenterModalProps> = ({
  open,
  onClose,
  title,
  children,
  width,
  size = "md",
  hideCloseButton = false,
  paperSx,
  headerSx,
  contentSx,
  titleSx,
  closeButtonSx,
  disableBackdropClick = false,
}) => {
  const theme = useTheme();
  const sizeTokens = getCenterModalSizeTokens(size);

  const handleClose = (
    event: object,
    reason?: "backdropClick" | "escapeKeyDown",
  ) => {
    if (disableBackdropClick && reason === "backdropClick") return;
    onClose(event, reason);
  };
  const resolvedWidth = resolveCenterModalWidth(
    width ?? sizeTokens.defaultMdWidth,
    size,
  );

  const paperSxParts = paperSx ? (Array.isArray(paperSx) ? paperSx : [paperSx]) : [];
  const headerSxParts = headerSx ? (Array.isArray(headerSx) ? headerSx : [headerSx]) : [];
  const contentSxParts = contentSx ? (Array.isArray(contentSx) ? contentSx : [contentSx]) : [];
  const titleSxParts = titleSx ? (Array.isArray(titleSx) ? titleSx : [titleSx]) : [];
  const closeButtonSxParts = closeButtonSx
    ? Array.isArray(closeButtonSx)
      ? closeButtonSx
      : [closeButtonSx]
    : [];

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      sx={{
        "& .MuiBackdrop-root": {
          bgcolor: theme.palette.custom.others.modalBackgroundBlack,
        },
      }}
    >
      <Box
        sx={
          [style, resolvedWidth, { p: 0 }, ...paperSxParts] as SxProps<Theme>
        }
      >
        {/* Header */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={
            [
              centerModalHeaderBaseSx(theme),
              ...headerSxParts,
            ] as SxProps<Theme>
          }
        >
          <Typography
            id="modal-title"
            variant="h2"
            sx={
              [
                {
                  fontSize: sizeTokens.titleFontSize,
                  fontWeight: 700,
                  color: theme.palette.custom.gray[900],
                  lineHeight: 1.33,
                  letterSpacing: "-0.01em",
                },
                ...titleSxParts,
              ] as SxProps<Theme>
            }
          >
            {title}
          </Typography>
          {!hideCloseButton && (
            <IconButton
              onClick={(e) => handleClose(e, "escapeKeyDown")}
              size="small"
              sx={
                [
                  {
                    color: theme.palette.custom.gray[700],
                    "&:hover": {
                      bgcolor: "transparent",
                      color: theme.palette.custom.primaryHover,
                    },
                    "&:active": {
                      bgcolor: "transparent",
                      color: theme.palette.custom.primaryPressed,
                    },
                    "& .MuiTouchRipple-ripple .MuiTouchRipple-child": {
                      bgcolor: theme.palette.custom.primarySecondaryPressed,
                    },
                    mr: -1,
                    mt: -0.5,
                  },
                  ...closeButtonSxParts,
                ] as SxProps<Theme>
              }
            >
              <ClientOnly>
                <CloseIcon />
              </ClientOnly>
            </IconButton>
          )}
        </Stack>

        {/* Content */}
        <Box
          sx={
            [
              centerModalContentBaseSx(theme),
              {
                flex: 1,
                overflowY: "auto",
                // iOS Safari zooms the viewport when a focused control's computed font-size
                // is below 16px. MUI `size="small"` and modal overrides often use 0.875rem.
                // !important wins over per-field `sx` (e.g. CreateProjectModal inputSx).
                [theme.breakpoints.down("md")]: {
                  // 16px literal: iOS threshold; 1rem can be <16px if html font-size was reduced.
                  "& .MuiInputBase-input": { fontSize: "16px !important" },
                  "& .MuiInputBase-inputMultiline": {
                    fontSize: "16px !important",
                  },
                  "& .MuiSelect-select": { fontSize: "16px !important" },
                  "& .MuiAutocomplete-input": { fontSize: "16px !important" },
                },
              },
              ...contentSxParts,
            ] as SxProps<Theme>
          }
        >
          {children}
        </Box>
      </Box>
    </Modal>
  );
};

export type { CenterModalSize } from "./tokens";
